import { FOCUS_RING, getPasswordManagerAttributes, Icon } from '@amelio/ui-library';
import { useId } from 'react';

interface TranslationFieldProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    maxLength: number;
    placeholder: string;
    /** Already translated: names what is too long, in the consumer's own wording. */
    overLimitMessage: string;
    /** Already translated: shown under an empty field. */
    missingTranslationLabel: string;
    /** `textarea` for a longer text (e.g. a description); `input` for a short one (e.g. a name). */
    multiline?: boolean;
    /** Ties the label to the field explicitly; generated when the caller has no stable id of its
     * own (e.g. one built from the field's name and language). */
    id?: string;
    dataTestId?: string;
}

/**
 * One language's translation for a single field: label, input, character counter, and an
 * over-limit or missing-translation hint. Shared by every "translate this field per language"
 * tab (`ValueTranslationsTab`'s name/description, `StrategicVisionTranslationsTab`'s vision)
 * so each one only supplies what differs -- its label, limit and wording.
 */
export function TranslationField({
    label,
    value,
    onChange,
    maxLength,
    placeholder,
    overLimitMessage,
    missingTranslationLabel,
    multiline = false,
    id,
    dataTestId,
}: TranslationFieldProps) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const isOverLimit = value.length > maxLength;
    const fieldClassName = `text-navy rounded-lg border bg-white px-4 text-sm ${FOCUS_RING} ${
        isOverLimit ? 'border-red-500' : 'border-gray-300'
    }`;

    return (
        <div className={'flex flex-col gap-2'}>
            <label
                htmlFor={fieldId}
                className={'text-navy text-sm font-medium'}
            >
                {label}
            </label>

            {multiline ? (
                <textarea
                    {...getPasswordManagerAttributes()}
                    id={fieldId}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    data-testid={dataTestId}
                    className={`${fieldClassName} max-h-[120px] min-h-[80px] resize-none py-3`}
                />
            ) : (
                <input
                    {...getPasswordManagerAttributes()}
                    id={fieldId}
                    type={'text'}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    maxLength={maxLength}
                    placeholder={placeholder}
                    data-testid={dataTestId}
                    className={`${fieldClassName} h-12`}
                />
            )}

            {isOverLimit ? (
                <p className={'text-xs text-red-500'}>{overLimitMessage}</p>
            ) : !value ? (
                <div className={'flex items-center gap-1'}>
                    <Icon
                        name={'essential-info-circle'}
                        size={'xs'}
                        colorClassName={'text-amber-700'}
                    />
                    <span className={'text-xs text-amber-700'}>{missingTranslationLabel}</span>
                </div>
            ) : null}

            <p className={`text-right text-xs ${isOverLimit ? 'text-red-500' : 'text-gray-400'}`}>
                {value.length}/{maxLength}
            </p>
        </div>
    );
}
